import { SatMath } from '@app/app/analysis/sat-math';
import { ToastMsgType } from '@app/engine/core/interfaces';
import { t7e } from '@app/locales/keys';
import { CruncerMessageTypes } from '@app/webworker/positionCruncher';
import { getDayOfYear } from '@ootk/src/main';
import { keepTrackApi } from '../../keepTrackApi';
import { DateTimeManager } from '../../plugins/date-time-manager/date-time-manager';
import { EventBus } from '../events/event-bus';
import { EventBusEvent } from '../events/event-bus-events';
import { getEl } from '../utils/get-el';
export class TimeManager {
    datetimeInputDOM = null;
    /**
     * The real time at the moment when dynamicOffset or propRate changes
     */
    dynamicOffsetEpoch = null;
    lastPropRate = 1;
    propFrozen = 0;
    propOffset = 0;
    /**
     * The rate of change applied to the dynamicOffset
     */
    propRate = null;
    /**
     * The time in the real world
     */
    realTime = 0;
    selectedDate = new Date();
    /**
     * The time in the simulation
     *
     * simulationTime = realTime + staticOffset + dynamicOffset * propRate
     */
    simulationTimeObj = new Date();
    /**
     * The time offset ignoring propRate (ex. New Launch)
     */
    staticOffset;
    lastBoxUpdateTime = 0;
    /**
     * dynamicOffset: The time offset that is impacted by propRate
     *
     * dynamicOffset = realTime - dynamicOffsetEpoch
     */
    dynamicOffset_;
    gmst = 0;
    j;
    timeUntilChangingEnabled = 10000;
    isTimeChangingEnabled = false;
    isKeyboardBindingsInitialized_ = false;
    isInitialized = false;
    static currentEpoch(currentDate) {
        const currentDateObj = new Date(currentDate);
        const epochYear = currentDateObj.getUTCFullYear().toString().slice(2, 4);
        const epochDay = getDayOfYear(currentDateObj);
        const timeOfDay = (currentDateObj.getUTCHours() * 60 + currentDateObj.getUTCMinutes()) / 1440;
        const epochDayStr = (epochDay + timeOfDay).toFixed(8).padStart(12, '0');
        return [epochYear, epochDayStr];
    }
    // Propagation Time Functions
    calculateSimulationTime(newSimulationTime) {
        if (typeof newSimulationTime !== 'undefined' && newSimulationTime !== null) {
            this.simulationTimeObj.setTime(newSimulationTime.getTime());
            return this.simulationTimeObj;
        }
        if (this.propRate === 0) {
            const simulationTime = this.dynamicOffsetEpoch + this.staticOffset;
            this.simulationTimeObj.setTime(simulationTime);
        }
        else {
            this.realTime = Date.now();
            this.dynamicOffset_ = this.realTime - this.dynamicOffsetEpoch;
            const simulationTime = this.dynamicOffsetEpoch + this.staticOffset + this.dynamicOffset_ * this.propRate;
            this.simulationTimeObj.setTime(simulationTime);
        }
        keepTrackApi.emit(EventBusEvent.calculateSimulationTime, this.simulationTimeObj);
        return this.simulationTimeObj;
    }
    changePropRate(propRate, isShowToast = true) {
        if (this.propRate === propRate) {
            return;
        } // no change
        this.propRate = propRate;
        keepTrackApi.emit(EventBusEvent.propRateChanged, this.propRate);
        if (!this.isInitialized) {
            return;
        }
        this.staticOffset = this.simulationTimeObj.getTime() - Date.now();
        // Changing propRate or dynamicOffsetEpoch before calculating the staticOffset will give incorrect results
        this.dynamicOffsetEpoch = Date.now();
        this.calculateSimulationTime();
        this.synchronize();
        const toggleTimeDOM = getEl('toggle-time-rmb');
        if (keepTrackApi.getTimeManager().propRate === 0) {
            toggleTimeDOM.childNodes[0].textContent = 'Start Clock';
        }
        else {
            toggleTimeDOM.childNodes[0].textContent = 'Pause Clock';
        }
        const uiManagerInstance = keepTrackApi.getUiManager();
        if (!settingsManager.isAlwaysHidePropRate && isShowToast) {
            if (this.propRate > 1.01 || this.propRate < 0.99) {
                if (this.propRate < 10) {
                    uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.standby);
                }
                if (this.propRate >= 10 && this.propRate < 60) {
                    uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.caution);
                }
                if (this.propRate >= 60) {
                    uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.serious);
                }
            }
            else {
                uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.normal);
            }
        }
    }
    static isLeapYear(dateIn) {
        const year = dateIn.getUTCFullYear();
        if ((year & 3) !== 0) {
            return false;
        }
        return year % 100 !== 0 || year % 400 === 0;
    }
    changeStaticOffset(staticOffset) {
        this.dynamicOffsetEpoch = Date.now();
        this.staticOffset = staticOffset;
        this.calculateSimulationTime();
        this.synchronize();
        keepTrackApi.emit(EventBusEvent.staticOffsetChange, this.staticOffset);
    }
    getOffsetTimeObj(offset) {
        // Make a time variable
        const now = new Date();
        // Set the time variable to the time in the future
        now.setTime(this.simulationTimeObj.getTime() + offset);
        return now;
    }
    getPropOffset() {
        if (!this.selectedDate) {
            return 0;
        }
        // Not using local scope caused time to drift backwards!
        return this.selectedDate.getTime() - Date.now();
    }
    init() {
        this.dynamicOffsetEpoch = Date.now();
        this.simulationTimeObj = new Date();
        this.propFrozen = Date.now(); // for when propRate 0
        this.realTime = this.propFrozen; // (initialized as Date.now)
        this.propRate = settingsManager.propRate ?? 1.0; // time rate multiplier for propagation
        this.staticOffset = settingsManager.simulationTime ? settingsManager.simulationTime.getTime() - Date.now() : 0;
        // Initialize
        this.calculateSimulationTime();
        this.setSelectedDate(this.simulationTimeObj);
        this.initializeKeyboardBindings_();
        setTimeout(() => {
            this.isTimeChangingEnabled = true;
        }, this.timeUntilChangingEnabled);
        this.isInitialized = true;
    }
    update() {
        const { gmst, j } = SatMath.calculateTimeVariables(this.simulationTimeObj);
        this.gmst = gmst;
        this.j = j;
    }
    initializeKeyboardBindings_() {
        if (this.isKeyboardBindingsInitialized_) {
            return;
        }
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (key === 't' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                keepTrackApi.getUiManager().toast('Time Set to Real Time', ToastMsgType.normal);
                this.changeStaticOffset(0); // Reset to Current Time
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (key === ',' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                this.calculateSimulationTime();
                let newPropRate = this.propRate;
                if (this.propRate < 0.001 && this.propRate > -0.001) {
                    newPropRate = -0.001;
                }
                if (this.propRate < -1000) {
                    newPropRate = -1000;
                }
                if (newPropRate < 0) {
                    newPropRate = (this.propRate * 1.5);
                }
                else {
                    newPropRate = ((this.propRate * 2) / 3);
                }
                const calendarInstance = keepTrackApi.getPlugin(DateTimeManager)?.calendar;
                if (calendarInstance) {
                    calendarInstance.updatePropRate(newPropRate);
                }
                else {
                    this.changePropRate(newPropRate);
                }
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (key === '.' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                this.calculateSimulationTime();
                let newPropRate = this.propRate;
                if (this.propRate < 0.001 && this.propRate > -0.001) {
                    newPropRate = 0.001;
                }
                if (this.propRate > 1000) {
                    newPropRate = 1000;
                }
                if (newPropRate > 0) {
                    newPropRate = (this.propRate * 1.5);
                }
                else {
                    newPropRate = ((this.propRate * 2) / 3);
                }
                const calendarInstance = keepTrackApi.getPlugin(DateTimeManager)?.calendar;
                if (calendarInstance) {
                    calendarInstance.updatePropRate(newPropRate);
                }
                else {
                    this.changePropRate(newPropRate);
                }
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (key === '<' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                this.calculateSimulationTime();
                this.changeStaticOffset(this.staticOffset - settingsManager.changeTimeWithKeyboardAmountBig);
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (key === '>' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                this.calculateSimulationTime();
                this.changeStaticOffset(this.staticOffset + settingsManager.changeTimeWithKeyboardAmountBig);
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (key === '/' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                let newPropRate;
                if (this.propRate === 1) {
                    newPropRate = 0;
                }
                else {
                    newPropRate = 1;
                }
                const calendarInstance = keepTrackApi.getPlugin(DateTimeManager)?.calendar;
                if (calendarInstance) {
                    calendarInstance.updatePropRate(newPropRate);
                }
                else {
                    this.changePropRate(newPropRate);
                }
                this.calculateSimulationTime();
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (_key, code, isRepeat) => {
            if (code === 'Equal' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                this.calculateSimulationTime();
                this.changeStaticOffset(this.staticOffset + settingsManager.changeTimeWithKeyboardAmountSmall);
            }
        });
        EventBus.getInstance().on(EventBusEvent.KeyDown, (_key, code, isRepeat) => {
            if (code === 'Minus' && !isRepeat) {
                if (!this.isTimeChangingEnabled) {
                    keepTrackApi.getUiManager().toast(t7e('errorMsgs.catalogNotFullyInitialized'), ToastMsgType.caution, true);
                    return;
                }
                this.calculateSimulationTime();
                this.changeStaticOffset(this.staticOffset - settingsManager.changeTimeWithKeyboardAmountSmall);
            }
        });
        this.isKeyboardBindingsInitialized_ = true;
    }
    setNow(realTime) {
        this.realTime = realTime;
        // NOTE: This should be the only regular call to calculateSimulationTime!!
        this.calculateSimulationTime();
    }
    toggleTime() {
        if (this.propRate === 0) {
            this.changePropRate(this.lastPropRate);
        }
        else {
            this.lastPropRate = this.propRate;
            this.changePropRate(0);
        }
        const uiManagerInstance = keepTrackApi.getUiManager();
        if (this.propRate > 1.01 || this.propRate < 0.99) {
            if (this.propRate < 10) {
                uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.standby);
            }
            if (this.propRate >= 10 && this.propRate < 60) {
                uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.caution);
            }
            if (this.propRate >= 60) {
                uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.serious);
            }
        }
        else {
            uiManagerInstance.toast(`Propagation Speed: ${this.propRate.toFixed(1)}x`, ToastMsgType.normal);
        }
    }
    setSelectedDate(selectedDate) {
        this.selectedDate = selectedDate;
        EventBus.getInstance().emit(EventBusEvent.selectedDateChange, this.selectedDate);
    }
    synchronize() {
        const catalogManagerInstance = keepTrackApi.getCatalogManager();
        const orbitManagerInstance = keepTrackApi.getOrbitManager();
        keepTrackApi.emit(EventBusEvent.updateDateTime, new Date(this.dynamicOffsetEpoch + this.staticOffset));
        const message = {
            typ: CruncerMessageTypes.OFFSET,
            staticOffset: this.staticOffset,
            dynamicOffsetEpoch: this.dynamicOffsetEpoch,
            propRate: this.propRate,
        };
        catalogManagerInstance.satCruncher.postMessage(message);
        /*
         * OrbitWorker starts later than the satCruncher so it might not be
         * ready yet.
         */
        if (orbitManagerInstance.orbitWorker) {
            orbitManagerInstance.orbitWorker.postMessage(message);
        }
    }
    isLeapYear(date) {
        const year = date.getUTCFullYear();
        if ((year & 3) !== 0) {
            return false;
        }
        return year % 100 !== 0 || year % 400 === 0;
    }
    getUTCDayOfYear(doy) {
        const mn = doy.getUTCMonth();
        const dn = doy.getUTCDate();
        const dayCount = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
        let dayInYear = 365;
        let dayOfYear = dayCount[mn] + dn;
        if (mn > 1 && this.isLeapYear(doy)) {
            dayOfYear++;
            dayInYear++;
        }
        return dayOfYear % dayInYear;
    }
    getUTCDateFromDayOfYear(year, dayOfYear) {
        const isLeapYear = this.isLeapYear(this.createUTCDate(year, 0, 1));
        const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        let month = 0;
        while (dayOfYear > daysInMonth[month]) {
            dayOfYear -= daysInMonth[month];
            month++;
        }
        return this.createUTCDate(year, month, dayOfYear);
    }
    createUTCDate(year, month, day, hours, minutes, seconds) {
        const date = new Date(Date.UTC(year, month ?? 0, day ?? 1, hours ?? 0, minutes ?? 0, seconds ?? 0));
        return date;
    }
}
